import { Supplier, SupplierPrice, Quote, SupplierOrder, QuoteItem, SupplierOrderItem } from '../types';

// Mock data
const mockSuppliers: Supplier[] = [
  {
    id: 'sup-1',
    raisonSociale: 'Würth France',
    contactNom: 'Service Commercial',
    contactEmail: 'commandes@wurth.fr',
    contactTel: '03 88 64 64 64',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'sup-2',
    raisonSociale: 'Scieries du Nord SARL',
    contactNom: 'Pierre Forestier',
    contactEmail: 'vente@scieriesdunord.fr',
    contactTel: '03 20 45 67 89',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'sup-3',
    raisonSociale: 'Isover Saint-Gobain',
    contactNom: 'Service Négoce',
    contactEmail: 'negoce@isover.com',
    contactTel: '01 88 54 54 54',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'sup-4',
    raisonSociale: 'Sika France SAS',
    contactNom: 'Équipe Charpente',
    contactEmail: 'charpente@sika.fr',
    contactTel: '01 30 13 86 00',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  }
];

const mockSupplierPrices: SupplierPrice[] = [
  {
    id: 'price-1',
    supplierId: 'sup-1',
    productId: 'prod-1',
    prixUnitaireHT: 0.89,
    conditionnement: 200,
    remise: 15,
    dateEffet: '2024-01-01T00:00:00Z',
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'price-2',
    supplierId: 'sup-2',
    productId: 'prod-5',
    prixUnitaireHT: 18.50,
    conditionnement: 1,
    remise: 10,
    dateEffet: '2024-01-01T00:00:00Z',
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'price-3',
    supplierId: 'sup-3',
    productId: 'prod-7',
    prixUnitaireHT: 8.90,
    conditionnement: 1,
    remise: 12,
    dateEffet: '2024-01-01T00:00:00Z',
    createdAt: '2024-01-01T00:00:00Z'
  }
];

const mockQuotes: Quote[] = [
  {
    id: 'quote-1',
    supplierId: 'sup-1',
    status: 'draft',
    items: [
      { productId: 'prod-2', quantity: 20, unitPrice: 25.50, totalPrice: 510 }
    ],
    totalAmount: 510,
    validUntil: '2024-12-31T00:00:00Z',
    createdBy: '2',
    createdAt: '2024-12-15T10:00:00Z'
  }
];

const mockSupplierOrders: SupplierOrder[] = [
  {
    id: 'order-1',
    supplierId: 'sup-2',
    status: 'pending',
    items: [
      { productId: 'prod-1', quantity: 200, receivedQuantity: 0, unitPrice: 0.15, totalPrice: 30 }
    ],
    totalAmount: 30,
    orderDate: '2024-12-15T00:00:00Z',
    expectedDelivery: '2024-12-18T00:00:00Z',
    createdBy: '2',
    createdAt: '2024-12-15T09:00:00Z'
  }
];

class SupplierService {
  // Suppliers
  async getSuppliers(): Promise<Supplier[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [...mockSuppliers];
  }

  async getSupplier(id: string): Promise<Supplier | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockSuppliers.find(s => s.id === id) || null;
  }

  async createSupplier(supplier: Omit<Supplier, 'id' | 'createdAt'>): Promise<Supplier> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newSupplier: Supplier = {
      ...supplier,
      id: `sup-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    
    mockSuppliers.push(newSupplier);
    return newSupplier;
  }

  async updateSupplier(id: string, updates: Partial<Supplier>): Promise<Supplier> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockSuppliers.findIndex(s => s.id === id);
    if (index === -1) throw new Error('Fournisseur non trouvé');
    
    mockSuppliers[index] = { ...mockSuppliers[index], ...updates };
    return mockSuppliers[index];
  }

  async deleteSupplier(id: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const index = mockSuppliers.findIndex(s => s.id === id);
    if (index !== -1) {
      mockSuppliers.splice(index, 1);
    }
  }

  // Supplier Prices
  async getSupplierPrices(productId?: string, supplierId?: string): Promise<SupplierPrice[]> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    let filtered = [...mockSupplierPrices];
    
    if (productId) {
      filtered = filtered.filter(p => p.productId === productId);
    }
    
    if (supplierId) {
      filtered = filtered.filter(p => p.supplierId === supplierId);
    }
    
    return filtered;
  }

  async createSupplierPrice(price: Omit<SupplierPrice, 'id' | 'createdAt'>): Promise<SupplierPrice> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newPrice: SupplierPrice = {
      ...price,
      id: `price-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    
    mockSupplierPrices.push(newPrice);
    return newPrice;
  }

  // Quotes
  async getQuotes(): Promise<Quote[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [...mockQuotes];
  }

  async getQuote(id: string): Promise<Quote | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockQuotes.find(q => q.id === id) || null;
  }

  async createQuote(quote: Omit<Quote, 'id' | 'createdAt'>): Promise<Quote> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newQuote: Quote = {
      ...quote,
      id: `quote-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    
    mockQuotes.push(newQuote);
    return newQuote;
  }

  async updateQuote(id: string, updates: Partial<Quote>): Promise<Quote> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockQuotes.findIndex(q => q.id === id);
    if (index === -1) throw new Error('Devis non trouvé');
    
    mockQuotes[index] = { ...mockQuotes[index], ...updates };
    return mockQuotes[index];
  }

  async convertQuoteToOrder(quoteId: string): Promise<SupplierOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const quote = mockQuotes.find(q => q.id === quoteId);
    if (!quote) throw new Error('Devis non trouvé');
    
    const orderItems: SupplierOrderItem[] = quote.items.map(item => ({
      ...item,
      receivedQuantity: 0
    }));
    
    const newOrder: SupplierOrder = {
      id: `order-${Date.now()}`,
      supplierId: quote.supplierId,
      status: 'pending',
      items: orderItems,
      totalAmount: quote.totalAmount,
      orderDate: new Date().toISOString(),
      createdBy: quote.createdBy,
      createdAt: new Date().toISOString()
    };
    
    mockSupplierOrders.push(newOrder);
    
    // Marquer le devis comme accepté
    quote.status = 'accepted';
    
    return newOrder;
  }

  // Supplier Orders
  async getSupplierOrders(): Promise<SupplierOrder[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [...mockSupplierOrders];
  }

  async getSupplierOrder(id: string): Promise<SupplierOrder | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockSupplierOrders.find(o => o.id === id) || null;
  }

  async createSupplierOrder(order: Omit<SupplierOrder, 'id' | 'createdAt'>): Promise<SupplierOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newOrder: SupplierOrder = {
      ...order,
      id: `order-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    
    mockSupplierOrders.push(newOrder);
    return newOrder;
  }

  async updateSupplierOrder(id: string, updates: Partial<SupplierOrder>): Promise<SupplierOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockSupplierOrders.findIndex(o => o.id === id);
    if (index === -1) throw new Error('Commande non trouvée');
    
    mockSupplierOrders[index] = { ...mockSupplierOrders[index], ...updates };
    return mockSupplierOrders[index];
  }

  async receiveOrder(orderId: string, itemId: string, receivedQuantity: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const order = mockSupplierOrders.find(o => o.id === orderId);
    if (!order) throw new Error('Commande non trouvée');
    
    const item = order.items.find(i => i.productId === itemId);
    if (!item) throw new Error('Article non trouvé');
    
    item.receivedQuantity += receivedQuantity;
    
    // Vérifier si la commande est complètement reçue
    const allReceived = order.items.every(i => i.receivedQuantity >= i.quantity);
    const partiallyReceived = order.items.some(i => i.receivedQuantity > 0);
    
    if (allReceived) {
      order.status = 'completed';
    } else if (partiallyReceived) {
      order.status = 'partially_received';
    }
  }

  // Helper methods
  getSupplierName(supplierId: string): string {
    const supplier = mockSuppliers.find(s => s.id === supplierId);
    return supplier?.raisonSociale || 'Fournisseur inconnu';
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Brouillon',
      sent: 'Envoyé',
      accepted: 'Accepté',
      rejected: 'Refusé',
      pending: 'En attente',
      confirmed: 'Confirmé',
      partially_received: 'Partiellement reçu',
      completed: 'Terminé',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      partially_received: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }
}

export const supplierService = new SupplierService();